"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  Home, 
  Map, 
  Sparkles, 
  HeartHandshake, 
  Shield, 
  DollarSign,
  ArrowRight
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const HostPage = () => {
  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Hero */}
      <section className="bg-gray-50 py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-flex items-center gap-2 bg-[#FFAE00]/10 text-[#FFAE00] px-4 py-2 rounded-full font-bold text-sm"
            >
              <Home className="w-4 h-4" /> HOST PROGRAM
            </motion.div>
            <h1 className="text-4xl md:text-6xl font-bricolage font-bold text-[#063258] leading-tight">
              Host the <span className="text-[#FFAE00]">perfect tribe</span> at your property.
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
              Do you own a boutique villa, a unique stay, or are you a local expert? Partner with us to bring curated, respectful, and high-quality groups to your doorstep.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link href="https://forms.gle/host-form" target="_blank">
                <Button className="bg-[#063258] hover:bg-[#0D0D0D] text-white px-10 py-7 rounded-2xl text-xl font-bold shadow-lg">
                  Partner with Us
                </Button>
              </Link>
              <Link href="tel:+917982579691">
                <Button variant="outline" className="border-gray-200 px-10 py-7 rounded-2xl text-xl font-bold hover:bg-gray-100 transition-all">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
          <div className="flex-1 relative">
             <div className="absolute inset-0 bg-[#FFAE00] rounded-[3rem] rotate-6 scale-95 opacity-20 -z-10"></div>
             <div className="bg-white p-4 rounded-[3rem] shadow-2xl border border-gray-100">
               <div className="bg-gray-100 rounded-[2.5rem] h-[400px] flex items-center justify-center text-gray-400">
                 {/* Image placeholder or actual image can go here */}
                 <Home className="w-20 h-20 opacity-20" />
               </div>
             </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <h2 className="text-4xl font-bricolage font-bold text-[#063258] text-center mb-20">Why host with Outcome Outing?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            {
              icon: <Sparkles className="text-[#FFAE00]" />,
              title: "Quality Over Quantity",
              description: "Our groups are handpicked and mentor-led. They are respectful, professional, and value unique experiences."
            },
            {
              icon: <DollarSign className="text-[#FFAE00]" />,
              title: "Consistent Revenue",
              description: "Enjoy predictable bookings through our curated trips, especially during weekdays and off-seasons."
            },
            {
              icon: <Shield className="text-[#FFAE00]" />,
              title: "Safety & Security",
              description: "We handle the entire coordination and ensure our guests follow the property guidelines strictly."
            }
          ].map((benefit, i) => (
            <div key={i} className="space-y-6 flex flex-col items-center text-center group">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center group-hover:bg-[#FFAE00]/10 transition-colors">
                {benefit.icon}
              </div>
              <h3 className="text-2xl font-bricolage font-bold">{benefit.title}</h3>
              <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Local Expert Section */}
      <section className="py-24 px-6 bg-[#063258] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <Map className="w-16 h-16 text-[#FFAE00] mx-auto mb-8" />
          <h2 className="text-3xl md:text-5xl font-bricolage font-bold mb-8">Are you a local hidden gem?</h2>
          <p className="text-xl opacity-80 mb-12 leading-relaxed">
            If you know the secret trails, the best local cafes, or the untold stories of your city, we&apos;d love to collaborate. You don&apos;t need a property to be a Local Host—you just need the passion for your roots.
          </p>
          <Link href="https://wa.me/917982579691" target="_blank">
            <Button className="bg-[#FFAE00] hover:bg-orange-500 text-white rounded-2xl px-12 py-8 text-xl font-bold flex items-center gap-3 mx-auto transition-transform hover:scale-105">
              Chat with our Curator <ArrowRight />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HostPage;
